import { isPresent } from "@apl-digital/utils";
import { isAnyOf } from "@reduxjs/toolkit";
import { deepEqual } from "fast-equals";

import { getUnixDateFromUTC } from "@/base/utils/dateHelpers";
import { clearStorage, setStorage, StorageKey } from "@/constants/storage";
import { hasData } from "@/store/apiRequestStatusMachine";
import type { AppStartListening } from "@/store/listenerMiddleware";

import { isStatus } from "../utils";

import {
  addProductToCart,
  refreshCart,
  removeAllProductsFromCart,
  removeCustomerFromCart,
  removeLinesFromCart,
  removeProductFromCart,
  updateCustomerCart,
  updateShoppingCartLanguage,
} from "./actions";
import { isShoppingCartExpired } from "./helper";
import {
  selectCurrentTransactionId,
  selectNewShoppingCart,
  selectShoppingCart,
} from "./slice";

export const addShoppingCartListeneres = (
  startListening: AppStartListening,
) => {
  startListening({
    predicate: (_action, currentState, previousState) =>
      !deepEqual(
        previousState.shoppingCart.shoppingCart,
        currentState.shoppingCart.shoppingCart,
      ),
    effect: (_action, { getState }) => {
      const shoppingCart = selectShoppingCart(getState());

      if (hasData(shoppingCart) && !isShoppingCartExpired(shoppingCart.state)) {
        setStorage(StorageKey.CART, JSON.stringify(shoppingCart.state), {
          type: "expiringRecord",
          expiresAt: getUnixDateFromUTC(shoppingCart.state.dttmExpiresUTC),
        });
      } else {
        clearStorage(StorageKey.CART);
      }
    },
  });

  // Associate customer with cart when user logs in
  startListening({
    predicate: (_action, currentState, previousState) =>
      !deepEqual(previousState.user.userData, currentState.user.userData) &&
      hasData(currentState.user.userData) &&
      isPresent(currentState.shoppingCart.shoppingCart),
    effect: async (_action, { getState, dispatch }) => {
      const transactionId = selectCurrentTransactionId(getState());
      const userState = getState().user.userData;

      if (hasData(userState) && isPresent(transactionId)) {
        await dispatch(
          updateCustomerCart({
            transactionId,
            body: {
              CustomerPersonID: userState.state.ID,
              LoyaltyProgramMembershipID: 0,
            },
          }),
        );
      }
    },
  });

  // Remove the customer from the cart after auth resolves to guest.
  // On logout, this runs after the redirect returns so the request is not
  // interrupted by navigation away from the app.
  startListening({
    predicate: (_action, currentState, previousState) => {
      if (
        hasData(currentState.user.userData) ||
        !currentState.user.isAuthReady
      ) {
        return false;
      }

      if (!hasData(currentState.shoppingCart.shoppingCart)) {
        return false;
      }

      // After SSO redirect return, auth becomes ready with no logged-in user
      const authJustBecameReady =
        !previousState.user.isAuthReady && currentState.user.isAuthReady;

      return authJustBecameReady;
    },
    effect: async (_action, { getState, dispatch }) => {
      const shoppingCart = selectShoppingCart(getState());

      if (
        !hasData(shoppingCart) ||
        shoppingCart.state.CustomerPerson?.ID <= 0
      ) {
        return;
      }

      await dispatch(
        removeCustomerFromCart({
          transactionId: shoppingCart.state.ID,
        }),
      );
    },
  });

  startListening({
    matcher: isAnyOf(
      addProductToCart.fulfilled,
      removeLinesFromCart.fulfilled,
      removeProductFromCart.fulfilled,
      removeAllProductsFromCart.fulfilled,
      updateShoppingCartLanguage.fulfilled,
    ),
    effect: async (_action, { dispatch, getState }) => {
      const transactionId = selectCurrentTransactionId(getState());
      const newShoppingCart = selectNewShoppingCart(getState());

      if (isPresent(transactionId) && isStatus(newShoppingCart, "idle")) {
        await dispatch(refreshCart({ transactionId }));
      }
    },
  });

  startListening({
    predicate: (_action, currentState, previousState) =>
      previousState.shoppingCart.shoppingCartLanguage !==
      currentState.shoppingCart.shoppingCartLanguage,
    effect: async (_action, { getState, dispatch }) => {
      const shoppingCartLanguage = getState().shoppingCart.shoppingCartLanguage;

      if (isPresent(shoppingCartLanguage)) {
        const transactionId = selectCurrentTransactionId(getState());

        setStorage(StorageKey.SHOPPING_CART_LANGUAGE, shoppingCartLanguage);

        if (!isPresent(transactionId)) {
          return;
        }

        await dispatch(
          updateShoppingCartLanguage({
            transactionId,
            body: {
              language: shoppingCartLanguage,
            },
          }),
        );
      } else {
        clearStorage(StorageKey.SHOPPING_CART_LANGUAGE);
      }
    },
  });
};
